import React, { useRef, useState } from 'react';
import { Alert, Box, Button, Card, CardContent, Menu, MenuItem, Tab, Tabs, Typography } from '@mui/material';
import { Assessment as ReportIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { useAccessControl } from '../../hooks/useAccessControl';
import { RESOURCES, ACTIONS } from '../../hooks/usePermissions';
import ModulePageHeader from '../common/ModulePageHeader';
import CustomerLedgerTab from './receivables/CustomerLedgerTab';
import InvoicesTab from './receivables/InvoicesTab';
import PaymentsTab from './receivables/PaymentsTab';
import ProvisionsTab from './receivables/ProvisionsTab';
import WriteOffsTab from './receivables/WriteOffsTab';
import RecoveriesTab from './receivables/RecoveriesTab';
import { AR_REPORT_ITEMS, AR_REPORTS_DISCLAIMER } from './receivables/ArReportsTab';
import FeeSyncPanel from './receivables/FeeSyncPanel';


const TABS = [
  { value: 'customerLedger', label: 'Customer Ledger', Component: CustomerLedgerTab },
  { value: 'invoices', label: 'Invoices', Component: InvoicesTab },
  { value: 'payments', label: 'Payments', Component: PaymentsTab },
  { value: 'provisions', label: 'Provisions', Component: ProvisionsTab },
  { value: 'writeOffs', label: 'Write-offs', Component: WriteOffsTab },
  { value: 'recoveries', label: 'Recoveries', Component: RecoveriesTab },
];

// Right-aligned "Reports" dropdown - same anchored-menu pattern as
// CoaAccounts.js's ConfigMenu / CashBank.js's ConfigMenu: the button shows
// the active report's label once one is selected, otherwise "Reports".
const ReportsMenu = ({ tab, setTab }) => {
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const isReportTab = AR_REPORT_ITEMS.some((i) => i.value === tab);
  const activeLabel = AR_REPORT_ITEMS.find((i) => i.value === tab)?.label;

  return (
    <>
      <Button
        ref={anchorRef}
        size="small"
        variant={isReportTab ? 'contained' : 'outlined'}
        endIcon={<ExpandMoreIcon />}
        startIcon={<ReportIcon />}
        onClick={() => setOpen((o) => !o)}
        onMouseEnter={() => setOpen(true)}
        sx={{ textTransform: 'none', fontWeight: isReportTab ? 700 : 400, whiteSpace: 'nowrap' }}
      >
        {isReportTab ? activeLabel : 'Reports'}
      </Button>
      <Menu
        anchorEl={anchorRef.current}
        open={open}
        onClose={() => setOpen(false)}
        MenuListProps={{ onMouseLeave: () => setOpen(false) }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ elevation: 2, sx: { minWidth: 210, mt: 0.5 } }}
      >
        {AR_REPORT_ITEMS.map((item) => (
          <MenuItem
            key={item.value}
            selected={tab === item.value}
            onClick={() => { setTab(item.value); setOpen(false); }}
            sx={{ fontSize: '0.875rem' }}
          >
            {item.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

// Student Receivables: the per-student AR subledger (config/ledger.php
// section 'receivables') - balances, invoices and receipts synced from the
// school's fee billing/collection flow (see SchoolAccounting.php). Distinct
// from Fees & Billing (ledger-fees), which is the day-to-day collection
// screen; this is the accountant's AR oversight view.
const StudentReceivables = () => {
  const { can } = useAccessControl();
  const [tab, setTab] = useState('customerLedger');

  if (!can(RESOURCES.LEDGER.RECEIVABLES, ACTIONS.READ)) {
    return <Alert severity="error">You do not have permission to view Student Receivables.</Alert>;
  }

  const isDocTab = TABS.some((t) => t.value === tab);
  const ActiveDocTab = TABS.find((t) => t.value === tab)?.Component;
  const ActiveReport = AR_REPORT_ITEMS.find((t) => t.value === tab)?.Component;

  return (
    <Box>
      <ModulePageHeader
        title="Student Receivables"
        subtitle="Per-student AR balances, invoices and receipts - synced from fee billing and collection"
      />
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ pb: '8px !important', pt: '4px !important' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
            <Tabs
              value={isDocTab ? tab : false}
              onChange={(_, v) => v && setTab(v)}
              variant="scrollable"
              scrollButtons="auto"
            >
              {TABS.map((t) => (
                <Tab key={t.value} value={t.value} label={t.label} />
              ))}
            </Tabs>
            <ReportsMenu tab={tab} setTab={setTab} />
          </Box>
        </CardContent>
      </Card>

      {tab === 'customerLedger' && <FeeSyncPanel />}
      {ActiveDocTab && <ActiveDocTab />}
      {ActiveReport && (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {AR_REPORTS_DISCLAIMER}
          </Typography>
          <ActiveReport />
        </Box>
      )}
    </Box>
  );
};

export default StudentReceivables;
